import {useEffect, useRef} from 'react';

import {propagateErrorsContext} from '../../util/errors';
import {usePollInterval} from '../polling/hooks';
import {
  type OperationVariables,
  type QueryHookOptions,
} from './apolloWithAbort';
import {useQuery as useApolloQuery} from './apolloWithAbort';

// Our version of useQuery. It returns a loading state that can
// be used in a type-guard to ensure we get non-null data values
// when loading is false.

export type UseQueryOptions<
  D = any,
  V extends OperationVariables = OperationVariables,
> = Omit<QueryHookOptions<D, V>, 'pollInterval'> & {
  enablePolling?: boolean;
  pollMultiplier?: number;
};

export function useQuery<D, V extends OperationVariables>(
  document: any,
  options: UseQueryOptions<D, V>
) {
  const globalPollInterval = usePollInterval();
  const isSkipped = Boolean(options.skip);

  let pollInterval = isSkipped
    ? 0
    : options.enablePolling
      ? globalPollInterval *
        (options.pollMultiplier != null ? options.pollMultiplier : 1)
      : 0;
  if (!isSkipped && pollInterval === 0) {
    pollInterval = 365 * 24 * 60 * 60000;
  }

  const {loading, data, refetch, networkStatus, fetchMore, error} =
    useApolloQuery<D, V>(document, {
      ...options,
      context: {...propagateErrorsContext(), ...options.context},
      pollInterval,
    });

  // Apollo can leave a query idle when it changes from skipped to active.
  const wasSkippedRef = useRef(isSkipped);

  useEffect(() => {
    const justActivated = wasSkippedRef.current && !isSkipped;
    wasSkippedRef.current = isSkipped;

    if (justActivated && !loading && data == null) {
      refetch();
    }
  }, [loading, data, isSkipped, refetch]);

  return {
    loading,
    data,
    refetch,
    networkStatus,
    fetchMore,
    error,
    initialLoading: Boolean(networkStatus === 1 || options.skip),
  };
}
